import { ReactNode, memo } from 'react';
import { Layout, Form, FormProps } from 'antd';
import classNames from 'classnames';
import { User, RequisitionInfo } from 'types';

import { FooterActions } from '@Enum/index';
import { Informative, MTextArea } from '@Components/Base';
import { ReactComponent as NextArrowIcon } from '@Icons/additional/arrow-left.svg';
import { useHeaderCaption } from '@Hooks/useHeaderCaption';
import { Balance } from '@Types/api/form/leaveRequest/Balance';
import { FormType, HeaderStepperOptions } from '@Components/MainForm/types';

import InformationSidebar from '../components/InformationSidebar/InformationSidebar';
import HeaderStepper from '../components/HeaderStepper/HeaderStepper';
import RequisitionInformation from '../components/RequisitionInformation/RequisitionInformation';
import FooterButtonSection from '../components/FooterButtonSection/FooterButtonSection';
import styles from '../main-form.module.css';

interface MainFormProps {
  userInfo: User;
  requisitionInfo: RequisitionInfo;
  formType: FormType;
  selectedUser?: User;
  disabledSaveForm?: boolean;
  disabledNextStep?: boolean;
  descriptionAlert?: JSX.Element;
  showSelectEmployee?: boolean;
  showLeaveBalance?: boolean;
  layout?: FormProps['layout'];
  children?: ReactNode;
  className?: string;
  handleSubmit?: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  handleSearchEmpByNumber?: (empNumber?: string) => void;
  handleApplySelectedUser?: () => void;
  onFooterAction: (action: FooterActions, payload?: string) => void;
  showExtraUserFields?: boolean;
  showMRsection?: boolean;
  balance?: Balance;
  headerStepperOptions?: HeaderStepperOptions;
  requestHeaderStep?: boolean;
  approveBtnName?: string;
}

const { Content } = Layout;

const MainForm: React.FC<MainFormProps> = ({
  handleSubmit,
  children,
  className,
  layout = 'vertical',
  userInfo,
  selectedUser,
  requisitionInfo,
  showLeaveBalance = false,
  showSelectEmployee = false,
  disabledSaveForm = false,
  disabledNextStep = true,
  onFooterAction,
  handleSearchEmpByNumber,
  handleApplySelectedUser,
  descriptionAlert,
  formType,
  showExtraUserFields = false,
  showMRsection = false,
  balance,
  headerStepperOptions,
  requestHeaderStep,
  approveBtnName,
}) => {
  const headerCaption = useHeaderCaption(false);

  const handleBack = (): void => onFooterAction(FooterActions.BACK_TO_REQUESTS);

  return (
    <Layout className={styles.layoutContainer}>
      <Layout>
        <Content>
          <div className={styles.formBackSection}>
            <NextArrowIcon onClick={handleBack} />
            <span>{headerCaption}</span>
          </div>
          <RequisitionInformation
            requisitionDate={requisitionInfo.requisitionDate}
            requisitionNo={requisitionInfo.requisitionNo}
            requisitionStatus={requisitionInfo.requisitionStatus}
          />
          <Form
            className={classNames(styles.form, className)}
            layout={layout}
            onFinish={handleSubmit}
            requiredMark={false}
          >
            {headerStepperOptions && (
              <HeaderStepper options={headerStepperOptions} />
            )}
            <div
              className={classNames(
                headerStepperOptions
                  ? styles.formContentWithHeaderStepper
                  : styles.formContent
              )}
            >
              {children}
              {descriptionAlert && (
                <div style={{ margin: '24px 0px' }}>
                  <Informative description={descriptionAlert} />
                </div>
              )}
              {formType === 'approval' && (
                <MTextArea
                  placeholder="Leave a comment"
                  onChange={(event) => {
                    onFooterAction(
                      FooterActions.COMMENTS_CHANGE,
                      event.target.value
                    );
                  }}
                  showCount
                  style={{ height: 120 }}
                  maxLength={100}
                />
              )}
            </div>
            <div className={classNames(styles.formFooterContent)}>
              <FooterButtonSection
                disabledNextStep={disabledNextStep}
                disabledSaveForm={disabledSaveForm}
                formType={formType}
                onFooterAction={onFooterAction}
                approveBtnName={approveBtnName}
              />
            </div>
          </Form>
        </Content>
      </Layout>
      <InformationSidebar
        showMRsection={showMRsection}
        userInfo={userInfo}
        selectedUser={selectedUser}
        showLeaveBalance={showLeaveBalance}
        balance={balance}
        showSelectEmployee={showSelectEmployee}
        handleSearchEmpByNumber={handleSearchEmpByNumber}
        handleApplySelectedUser={handleApplySelectedUser}
        showExtraUserFields={showExtraUserFields}
        requestHeaderStep={requestHeaderStep}
      />
    </Layout>
  );
};

export default memo(MainForm);
